import {
  ContextMenu,
  DataTree,
  DataTreeProps,
  Panel,
  Separator,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import React, { memo, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TPrivsTreeConnectorProps } from './PrivsTreeContainer';
import TreeTitledContainer from './TreeTitledContainer';
import { useTree } from './useTree';
import {
  PrivsTreeType,
  SEPARATOR,
  TreeCommand,
  TreeCommandName,
  useTreeCommands,
} from './useTreeCommands';
import { useTreeContextMenu } from './useTreeContextMenu';

const messages = defineMessages({
  catalog: { id: 'core.category.tree.catalogs', defaultMessage: 'catalog' },
  category: { id: 'core.appMenu.categories', defaultMessage: 'categories' },
  hierarchy: {
    id: 'core.category.tree.hierarchy',
    defaultMessage: 'hierarchy',
  },
  subdivision: {
    id: 'core.rolePrivs.subdivisionTitle',
    defaultMessage: 'subdivision',
  },
});

const PrivsTree: React.FC<TPrivsTreeConnectorProps> = ({
  className,
  isCategoryTree,
  pane,
  tree,
  onCheckToggle,
  onCollapse,
  onExpand,
  onNext,
  onPrev,
  onSelect,
  ...contextMenuHandlers
}) => {
  const intl = useIntl();
  const { activeIndex, handle } = useTree(tree);
  const { isOpen, setOpen, onContextMenu, positionX, positionY } =
    useTreeContextMenu({ selectNode: onSelect });

  const handleCheckToggle: DataTreeProps<TDatabaseId>['onCheckToggle'] = (
    _e,
    id
  ) => {
    onCheckToggle(id ?? tree.nodes[activeIndex].id);
  };

  const title = useMemo(
    () =>
      pane === 'all' || isCategoryTree
        ? intl.formatMessage(messages.category)
        : intl.formatMessage(messages[pane]),

    [intl, isCategoryTree, pane]
  );

  const { commands } = useTreeCommands({
    treeType: PrivsTreeType,
    activeNode: tree.nodes[activeIndex],
    isChecked: tree.checkedNodes.includes(tree.nodes[activeIndex].id),
    pane,
    onCheckToggle,
    ...contextMenuHandlers,
  });

  const options = useMemo<Array<TreeCommand | Separator> | undefined>(
    () =>
      !isCategoryTree && pane !== 'all' && commands
        ? [
            commands[TreeCommandName.GRANT_ALL_CATALOG_PRIVS],
            commands[TreeCommandName.GRANT_ALL_CATEGORY_PRIVS],
            commands[TreeCommandName.REVOKE_ALL_CATEGORY_PRIVS],
            SEPARATOR,
            commands[TreeCommandName.REFRESH],
          ].filter(cmd => !!cmd)
        : undefined,
    [commands, isCategoryTree, pane]
  );

  return !tree ? null : (
    <Panel className={className}>
      <TreeTitledContainer commands={options} title={title}>
        <DataTree
          hideCheckbox={isCategoryTree}
          activeId={tree.activeId}
          checked={tree.checkedNodes}
          data={tree.nodes}
          expanded={tree.expandedNodes}
          pending={tree.pendingNodes}
          ref={handle}
          onCollapse={onCollapse}
          onExpand={onExpand}
          onSelect={onSelect}
          onNext={onNext}
          onPrev={onPrev}
          onCheckToggle={handleCheckToggle}
          onContextMenu={onContextMenu}
        />
        {!isCategoryTree &&
          pane !== 'all' &&
          activeIndex !== -1 &&
          commands && (
            <ContextMenu
              x={positionX}
              y={positionY}
              open={isOpen}
              onOpen={setOpen}
              onClick={(commandName: string) =>
                commands[commandName].handler?.()
              }
              options={options}
            />
          )}
      </TreeTitledContainer>
    </Panel>
  );
};

export default memo(PrivsTree);
